import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const DiscountCodesManagement = () => {
  const theme = useThemeClasses();
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_order_amount: 0,
    max_uses: null,
    max_uses_per_customer: null,
    valid_from: '',
    valid_until: '',
    is_active: true,
    applies_to: 'all',
    category_ids: [],
    product_ids: []
  });

  useEffect(() => {
    loadDiscountCodes();
  }, []);

  const loadDiscountCodes = async () => {
    try {
      console.log('Loading discount codes...');
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Discount codes loaded:', response.data);
      setDiscountCodes(response.data.discount_codes || []);
    } catch (error) {
      console.error('Failed to load discount codes:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Submitting discount code:', formData);
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingCode) {
        console.log('Updating existing code:', editingCode.id);
        const response = await axios.put(`${API}/api/admin/discount-codes/${editingCode.id}`, formData, { headers });
        console.log('Update response:', response.data);
        toast.success('Discount code updated successfully!');
      } else {
        console.log('Creating new discount code');
        const response = await axios.post(`${API}/api/admin/discount-codes`, formData, { headers });
        console.log('Create response:', response.data);
        toast.success('Discount code created successfully!');
      }
      
      setShowForm(false);
      setEditingCode(null);
      setFormData({
        code: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 0,
        min_order_amount: 0,
        max_uses: null,
        max_uses_per_customer: null,
        valid_from: '',
        valid_until: '',
        is_active: true,
        applies_to: 'all',
        category_ids: [],
        product_ids: []
      });
      console.log('Reloading discount codes...');
      await loadDiscountCodes();
      console.log('Discount codes reloaded');
    } catch (error) {
      console.error('Failed to save discount code:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save discount code');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || '',
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      min_order_amount: code.min_order_amount || 0,
      max_uses: code.max_uses,
      max_uses_per_customer: code.max_uses_per_customer,
      valid_from: code.valid_from || '',
      valid_until: code.valid_until || '',
      is_active: code.is_active,
      applies_to: code.applies_to || 'all',
      category_ids: code.category_ids || [],
      product_ids: code.product_ids || []
    });
    setShowForm(true);
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/admin/discount-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discount code deleted successfully!');
      loadDiscountCodes();
    } catch (error) {
      console.error('Failed to delete discount code:', error);
      toast.error('Failed to delete discount code');
    }
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <span className={`px-2 py-1 text-xs rounded bg-gray-200 ${theme.textPrimary}`}>Inactive</span>;
    }
    
    const now = new Date();
    const validFrom = code.valid_from ? new Date(code.valid_from) : null;
    const validUntil = code.valid_until ? new Date(code.valid_until) : null;
    
    if (validFrom && now < validFrom) {
      return <span className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800">Scheduled</span>;
    }
    
    if (validUntil && now > validUntil) {
      return <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">Expired</span>;
    }
    
    if (code.max_uses && code.current_uses >= code.max_uses) {
      return <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">Limit Reached</span>;
    }
    
    return <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Active</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading discount codes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">🎫 Discount Codes</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingCode(null);
            setFormData({
              code: '',
              description: '',
              discount_type: 'percentage',
              discount_value: 0,
              min_order_amount: 0,
              max_uses: null,
              max_uses_per_customer: null,
              valid_from: '',
              valid_until: '',
              is_active: true,
              applies_to: 'all',
              category_ids: [],
              product_ids: []
            });
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ Create Discount Code'}
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow`}>
          <h3 className="text-lg font-semibold mb-4">
            {editingCode ? 'Edit Discount Code' : 'Create New Discount Code'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium mb-2">Discount Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., SAVE20"
                  required
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Holiday Sale"
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Discount Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Discount Type *</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>

              {/* Discount Value */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Discount Value * {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={formData.discount_type === 'percentage' ? 100 : undefined}
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                  required
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Min Order Amount */}
              <div>
                <label className="block text-sm font-medium mb-2">Min Order Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Max Uses */}
              <div>
                <label className="block text-sm font-medium mb-2">Max Total Uses (blank = unlimited)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_uses || ''}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Unlimited"
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Max Uses Per Customer */}
              <div>
                <label className="block text-sm font-medium mb-2">Max Uses Per Customer</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_uses_per_customer || ''}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_customer: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Unlimited"
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Valid From */}
              <div>
                <label className="block text-sm font-medium mb-2">Valid From (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>

              {/* Valid Until */}
              <div>
                <label className="block text-sm font-medium mb-2">Valid Until (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className={`w-4 h-4 text-blue-600 ${theme.border} rounded focus:ring-blue-500`}
              />
              <label htmlFor="is_active" className={`ml-2 text-sm font-medium ${theme.textPrimary}`}>
                Active (Code can be used)
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {editingCode ? 'Update Code' : 'Create Code'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingCode(null);
                }}
                className={`px-6 py-2 bg-gray-300 ${theme.textPrimary} rounded hover:bg-gray-400`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Discount Codes Table */}
      <div className={`${theme.card} rounded-lg shadow overflow-hidden`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className={`${theme.bgSecondary}`}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Code</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Description</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Discount</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Uses</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Status</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase`}>Actions</th>
            </tr>
          </thead>
          <tbody className={`${theme.card} divide-y divide-gray-200`}>
            {discountCodes.length === 0 ? (
              <tr>
                <td colSpan="6" className={`px-6 py-8 text-center ${theme.textSecondary}`}>
                  No discount codes created yet
                </td>
              </tr>
            ) : (
              discountCodes.map((code) => (
                <tr key={code.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-bold text-blue-600">{code.code}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${theme.textPrimary}`}>{code.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-semibold ${theme.textPrimary}`}>
                      {code.discount_type === 'percentage' 
                        ? `${code.discount_value}% off`
                        : `$${code.discount_value} off`
                      }
                    </div>
                    {code.min_order_amount > 0 && (
                      <div className={`text-xs ${theme.textSecondary}`}>Min: ${code.min_order_amount}</div>
                    )}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                    {code.current_uses || 0}
                    {code.max_uses ? ` / ${code.max_uses}` : ' / ∞'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(code)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleEdit(code)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(code.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};